// Security event logging utility
import { getAuthToken } from './useSecureAuth';
import { secureFetch } from './csrf';

// Security event types
const SECURITY_EVENTS = {
  FAILED_LOGIN: 'failed_login',
  SUCCESSFUL_LOGIN: 'successful_login',
  SUSPICIOUS_ACTIVITY: 'suspicious_activity',
  MFA_SETUP: 'mfa_setup',
  MFA_VERIFICATION: 'mfa_verification',
  CSRF_VIOLATION: 'csrf_violation',
  XSS_ATTEMPT: 'xss_attempt',
  RATE_LIMIT_EXCEEDED: 'rate_limit_exceeded',
  PASSWORD_CHANGE: 'password_change',
  ACCOUNT_LOCKOUT: 'account_lockout'
};

// Log a security event
export const logSecurityEvent = async (event, details = {}) => {
  try {
    // Don't send sensitive data like passwords
    const safeDetails = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      ...details
    };
    
    // Remove sensitive fields
    delete safeDetails.password;
    delete safeDetails.token;
    delete safeDetails.csrfToken;
    
    // For security logs, we use a direct fetch without CSRF protection
    // to avoid issues during login when CSRF token is not yet available
    const token = getAuthToken();
    if (token) {
      await fetch('/api/security-logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          event,
          details: safeDetails
        })
      });
    }
  } catch (error) {
    // Silently fail to prevent disruption
    console.warn('Failed to log security event:', error);
  }
};

// Log failed login attempt
export const logFailedLogin = async (email, reason) => {
  await logSecurityEvent(SECURITY_EVENTS.FAILED_LOGIN, {
    email: email.replace(/^(.{2}).*(@.*)$/, '$1***$2'), // Mask email
    reason
  });
};

// Log successful login
export const logSuccessfulLogin = async (email, method = 'password') => {
  await logSecurityEvent(SECURITY_EVENTS.SUCCESSFUL_LOGIN, {
    email: email.replace(/^(.{2}).*(@.*)$/, '$1***$2'), // Mask email
    method
  });
};

// Log suspicious activity
export const logSuspiciousActivity = async (action, details = {}) => {
  await logSecurityEvent(SECURITY_EVENTS.SUSPICIOUS_ACTIVITY, {
    action,
    ...details
  });
};

// Log MFA setup
export const logMFASetup = async (method) => {
  await logSecurityEvent(SECURITY_EVENTS.MFA_SETUP, {
    method
  });
};

// Log MFA verification
export const logMFAVerification = async (method, success) => {
  await logSecurityEvent(SECURITY_EVENTS.MFA_VERIFICATION, {
    method,
    success
  });
};

// Log CSRF violation
export const logCSRFViolation = async (endpoint) => {
  await logSecurityEvent(SECURITY_EVENTS.CSRF_VIOLATION, {
    endpoint
  });
};

// Log XSS attempt
export const logXSSAttempt = async (input, source) => {
  await logSecurityEvent(SECURITY_EVENTS.XSS_ATTEMPT, {
    source,
    inputLength: input.length
  });
};

// Log rate limit exceeded
export const logRateLimitExceeded = async (endpoint, attempts) => {
  await logSecurityEvent(SECURITY_EVENTS.RATE_LIMIT_EXCEEDED, {
    endpoint,
    attempts
  });
};

// Log password change
export const logPasswordChange = async () => {
  await logSecurityEvent(SECURITY_EVENTS.PASSWORD_CHANGE);
};

// Log account lockout
export const logAccountLockout = async (email, duration) => {
  await logSecurityEvent(SECURITY_EVENTS.ACCOUNT_LOCKOUT, {
    email: email.replace(/^(.{2}).*(@.*)$/, '$1***$2'), // Mask email
    duration
  });
};

// Client-side security monitoring
export const setupSecurityMonitoring = () => {
  // Monitor for suspicious console usage
  const originalConsole = window.console;
  window.console = {
    ...originalConsole,
    log: function(...args) {
      // Check for potential XSS attempts in console logs
      args.forEach(arg => {
        if (typeof arg === 'string' && /<script|javascript:|on\w+\s*=/.test(arg)) {
          logXSSAttempt(arg, 'console_log');
        }
      });
      return originalConsole.log.apply(originalConsole, args);
    }
  };
  
  // Monitor for unauthorized access to localStorage
  const originalLocalStorage = window.localStorage;
  Object.defineProperty(window, 'localStorage', {
    get: function() {
      logSuspiciousActivity('localStorage_access');
      return originalLocalStorage;
    }
  });
  
  // Monitor for unauthorized access to sessionStorage
  const originalSessionStorage = window.sessionStorage;
  Object.defineProperty(window, 'sessionStorage', {
    get: function() {
      logSuspiciousActivity('sessionStorage_access');
      return originalSessionStorage;
    }
  });
  
  // Monitor for visibility of DevTools
  let devtools = {
    open: false,
    orientation: null
  };
  
  const threshold = 160;
  
  setInterval(() => {
    if (window.outerHeight - window.innerHeight > threshold || 
        window.outerWidth - window.innerWidth > threshold) {
      if (!devtools.open) {
        devtools.open = true;
        logSuspiciousActivity('devtools_opened');
      }
      devtools.orientation = window.outerWidth - window.innerWidth > threshold ? 'vertical' : 'horizontal';
    } else {
      devtools.open = false;
      devtools.orientation = null;
    }
  }, 500);
};

// Initialize security monitoring
if (process.env.NODE_ENV === 'production') {
  setupSecurityMonitoring();
}

export default {
  SECURITY_EVENTS,
  logSecurityEvent,
  logFailedLogin,
  logSuccessfulLogin,
  logSuspiciousActivity,
  logMFASetup,
  logMFAVerification,
  logCSRFViolation,
  logXSSAttempt,
  logRateLimitExceeded,
  logPasswordChange,
  logAccountLockout,
  setupSecurityMonitoring
};